import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Check,
  ChevronLeft,
  Copy,
  KeyRound,
  Loader2,
  Plug,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const statusStyles = {
  active: 'bg-[#67A43C]/10 text-[#3F7E25] border-[#67A43C]/25',
  pending: 'bg-[#E6B43C]/12 text-[#9A6A00] border-[#E6B43C]/30',
  revoked: 'bg-slate-100 text-slate-500 border-slate-200',
};

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('es-PE', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${statusStyles[status] || statusStyles.revoked}`}>
      {status === 'active' ? 'Conectado' : status === 'pending' ? 'Pendiente' : 'Revocado'}
    </span>
  );
}

function Integrations({ userId, setActiveComponent, returnComponent = 'Options' }) {
  const [partners, setPartners] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingSlug, setGeneratingSlug] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const activeByPartner = useMemo(() => {
    const map = {};
    connections.forEach((connection) => {
      const slug = connection.partner_slug;
      if (!map[slug] || connection.status === 'active') {
        map[slug] = connection;
      }
    });
    return map;
  }, [connections]);

  const fetchData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const [partnersRes, connectionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/integrations/partners`),
        axios.get(`${API_URL}/api/integrations/connections`, { params: { user_id: userId } }),
      ]);
      setPartners(partnersRes.data.partners || []);
      setConnections(connectionsRes.data.connections || []);
    } catch (err) {
      console.error('Error loading integrations:', err);
      setError('No se pudieron cargar las integraciones.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  async function generateToken(partnerSlug) {
    setGeneratingSlug(partnerSlug);
    setGeneratedToken(null);
    setCopied(false);
    setError('');
    try {
      const { data } = await axios.post(`${API_URL}/api/integrations/connections/token`, {
        user_id: userId,
        partner_slug: partnerSlug,
      });
      setGeneratedToken({
        partnerSlug,
        token: data.connection_token,
        expiresAt: data.expires_at,
      });
      await fetchData();
    } catch (err) {
      console.error('Error generating integration token:', err);
      setError(err.response?.data?.error || 'No se pudo generar el token.');
    } finally {
      setGeneratingSlug(null);
    }
  }

  async function copyToken() {
    if (!generatedToken?.token) return;
    await navigator.clipboard.writeText(generatedToken.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  async function revokeConnection(connectionId) {
    setRevokingId(connectionId);
    setError('');
    try {
      await axios.patch(`${API_URL}/api/integrations/connections/${connectionId}/revoke`, {
        user_id: userId,
      });
      await fetchData();
    } catch (err) {
      console.error('Error revoking integration:', err);
      setError(err.response?.data?.error || 'No se pudo revocar la integracion.');
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <button
            onClick={() => setActiveComponent(returnComponent)}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            <ChevronLeft className="h-5 w-5" />
            Volver
          </button>
          <div className="flex items-center gap-2 text-[#B1336E]">
            <Plug className="h-5 w-5" />
            <h1 className="text-lg font-extrabold font-montserrat">Integraciones</h1>
          </div>
          <button
            onClick={fetchData}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            title="Actualizar"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}

        {generatedToken && (
          <section className="mb-5 rounded-lg border border-[#E6B43C]/40 bg-[#E6B43C]/10 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-[#7A5600]">
                <KeyRound className="h-4 w-4" />
                <span className="text-sm font-extrabold">Token de conexion</span>
              </div>
              <span className="text-xs font-semibold text-slate-500">
                Expira {formatDate(generatedToken.expiresAt)}
              </span>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <code className="min-w-0 flex-1 break-all rounded-lg border border-[#E6B43C]/30 bg-white px-3 py-2 text-xs text-slate-700">
                {generatedToken.token}
              </code>
              <button
                onClick={copyToken}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#B1336E] px-4 py-2 text-sm font-bold text-white hover:bg-[#9B2E63]"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          </section>
        )}

        {loading ? (
          <div className="flex min-h-[280px] items-center justify-center rounded-lg border border-slate-200 bg-white">
            <Loader2 className="h-7 w-7 animate-spin text-[#B1336E]" />
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {partners.map((partner) => {
              const connection = activeByPartner[partner.slug];
              const isGenerating = generatingSlug === partner.slug;
              const isRevoking = connection && revokingId === connection.id;

              return (
                <section key={partner.slug} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#B1336E]/10 text-[#B1336E]">
                        <Plug className="h-5 w-5" />
                      </div>
                      <div>
                        <h2 className="text-base font-extrabold text-slate-900">{partner.name}</h2>
                        <p className="text-xs font-semibold text-slate-500">{partner.slug}</p>
                      </div>
                    </div>
                    {connection && <StatusPill status={connection.status} />}
                  </div>

                  {connection?.status === 'active' ? (
                    <div className="space-y-3">
                      <div className="rounded-lg border border-[#67A43C]/25 bg-[#67A43C]/10 p-3">
                        <div className="mb-1 flex items-center gap-2 text-sm font-bold text-[#3F7E25]">
                          <ShieldCheck className="h-4 w-4" />
                          {connection.external_store_name || connection.external_store_id || 'Tienda conectada'}
                        </div>
                        <p className="text-xs text-slate-500">
                          Token API: {connection.api_token_preview || 'Activo'}
                        </p>
                      </div>
                      <button
                        onClick={() => revokeConnection(connection.id)}
                        disabled={isRevoking}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-60"
                      >
                        {isRevoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        Revocar
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => generateToken(partner.slug)}
                      disabled={isGenerating}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#B1336E] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#9B2E63] disabled:opacity-60"
                    >
                      {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                      Generar token
                    </button>
                  )}

                  {connection?.status === 'pending' && (
                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#E6B43C]/30 bg-[#E6B43C]/10 px-3 py-2 text-xs font-semibold text-[#7A5600]">
                      <RotateCcw className="h-4 w-4" />
                      Token pendiente hasta {formatDate(connection.connection_token_expires_at)}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default Integrations;
